import { useState } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { BusinessHoursEditor } from "./BusinessHoursEditor";
import { MenuSource, MenuSourceTile } from "./MenuSourceTile";
import { AssignSourceModal, DesignOption } from "./AssignSourceModal";
import "./ListingsHomeTab.scss";

let nextMockId = 100;

interface Props {
  locationId?: number;
}

export function ListingsHomeTab({ locationId }: Props) {
  // Starts empty — the user assigns menus from the "No menu assigned" tile.
  const [menus, setMenus] = useState<MenuSource[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  // The tile being re-assigned, or null when assigning a brand-new menu from the empty tile.
  const [editingId, setEditingId] = useState<string | null>(null);

  const openAssign = (id: string | null) => {
    setEditingId(id);
    setAssignOpen(true);
  };

  const closeAssign = () => {
    setAssignOpen(false);
    setEditingId(null);
  };

  const handleSetSource = (design: DesignOption) => {
    if (editingId === null) {
      // Assigning a new menu from the empty tile.
      setMenus((prev) => [
        ...prev,
        {
          id: String(nextMockId++),
          name: design.name,
          thumbUrl: design.thumbUrl,
          publishedAt: "Apr, 19 2026",
          status: "not_live",
        },
      ]);
    } else {
      // Re-assigning the source of an existing menu tile.
      setMenus((prev) =>
        prev.map((m) =>
          m.id === editingId ? { ...m, name: design.name, thumbUrl: design.thumbUrl } : m
        )
      );
    }
    closeAssign();
  };

  const setStatus = (id: string, status: MenuSource["status"]) =>
    setMenus((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));

  const removeMenu = (id: string) => setMenus((prev) => prev.filter((m) => m.id !== id));

  return (
    <div className="listings-home">
      <section className="menus-section">
        <h2 className="menus-heading">Menus</h2>

        <div className="menus-grid">
          {menus.map((menu) => (
            <MenuSourceTile
              key={menu.id}
              menu={menu}
              onEdit={() => openAssign(menu.id)}
              onDelete={() => removeMenu(menu.id)}
              onGoLive={() => setStatus(menu.id, "live")}
              onPause={() => setStatus(menu.id, "paused")}
              onResume={() => setStatus(menu.id, "live")}
            />
          ))}

          {/* Trailing "No menu assigned" tile — assigns a new menu source. */}
          <div className="menu-tile empty">
            <div className="menu-tile-empty-inner">
              <div className="menu-tile-empty-label">No menu assigned</div>
              <Tooltip title="Assign a menu">
                <button
                  type="button"
                  className="menu-tile-empty-edit"
                  aria-label="Assign a menu"
                  onClick={() => openAssign(null)}
                >
                  <EditOutlined />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </section>

      <BusinessHoursEditor />

      <AssignSourceModal
        open={assignOpen}
        locationId={locationId}
        onClose={closeAssign}
        onSetSource={handleSetSource}
      />
    </div>
  );
}
